// 月曆首頁的狀態列（W58.6 · ADR-0212）—— **雙欄並排**。
//
// ```text
//   ┌───────────────────┬───────────────────┐
//   │ 👥 共同時間 · 3 段 │ 🌸 經期 7 天後  › │
//   └───────────────────┴───────────────────┘
//   ▸ 展開的共同時間列表（佔滿整行寬度）
//   ▸ 降級推算的說明（只在 preset / partial 時出現）
// ```
//
// **為什麼是兩欄而不是上下堆疊。** W58.5 的排法是兩個**各自 100% 寬**的元件：
// 上面 `SharedTimePanel`（有框線的整寬 `<section>`），下面 `CycleStrip`（一個
// 整寬膠囊）。寬度其實相同，但**外框體系不同**（一個有框線、一個只是膠囊），
// 高度也不同 —— 在視覺上讀成「兩張寬度不同的卡片」，而它們其實是同一寬。
// 使用者的反饋正是這個：「上下堆疊且寬度不一，造成版面失衡與垂直空間浪費」。
//
// **順序沒有變。** W58.5 把「共同時間」移到了最上方（它是「**我們的**」——
// 這個模組存在的理由），這一輪只改**同一橫列的排法**，不改誰在上面。
//
// **展開的列表在下面、佔滿寬度。** 量出來的：375px 下兩欄各 167.5px，而列表
// 一行要放「日期區間 62px + 圖示 14px + 標題 + N 天」，標題只剩約 67px。
//
// **降級說明沒有被刪掉。** 它從卡片**裡面**搬到狀態列**下面**，而且只在降級
// 推算時出現 —— 「卡片裡不要那行字」≠「這一頁不要說明推算怎麼來的」
// （DATA_MODEL §5）。見 `cycleState.ts` 的 `degradedBasis`。

import { useState } from "react";

import { CycleLateNotice } from "@/components/calendar/CycleLateNotice";
import { CycleStatusCard } from "@/components/calendar/CycleStatusCard";
import { SharedTimeCard, SharedTimeList } from "@/components/calendar/SharedTime";
import { degradedBasis } from "@/components/calendar/cycleState";
import type { CalendarMonth, SharedSegment } from "@/lib/api/calendar";

export interface CalendarStatusBarProps {
  month: CalendarMonth | undefined;
  /** 點某一段共同時間 → 開那一天的日詳情。 */
  onOpenSegment: (segment: SharedSegment) => void;
  /** 進 /calendar/cycle。 */
  onOpenCycle: () => void;
  /** 一鍵補記：開一個已經是週期類型的表單。 */
  onRecord: () => void;
}

export function CalendarStatusBar({
  month,
  onOpenSegment,
  onOpenCycle,
  onRecord,
}: CalendarStatusBarProps) {
  const [open, setOpen] = useState(false);

  const segments = month?.shared_segments ?? [];
  const projection = month?.cycle_inference?.projection ?? null;
  const basis = degradedBasis(projection);

  return (
    <div className="flex flex-col gap-s-2" data-testid="calendar-status-bar">
      {/* W58.1 — 「經期比預期晚了」的溫馨提示 + 一鍵補記。
          它在狀態列**上面**：那是需要被處理的事，狀態可以等。 */}
      <CycleLateNotice
        daysLate={month?.cycle_inference?.projection?.days_late ?? 0}
        onRecord={onRecord}
      />

      <div className="grid grid-cols-2 gap-s-2">
        <SharedTimeCard
          segments={segments}
          open={open}
          onToggle={() => setOpen((v) => !v)}
        />
        <CycleStatusCard projection={projection} onOpen={onOpenCycle} />
      </div>

      {open && segments.length > 0 ? (
        <SharedTimeList segments={segments} onOpenSegment={onOpenSegment} />
      ) : null}

      {basis ? (
        <p className="px-s-1 text-meta text-ink-3" data-testid="calendar-cycle-strip-basis">
          {basis}
        </p>
      ) : null}
    </div>
  );
}
